// Public platform status (ARCHITECTURE 4.1): GET /api/platform/status.
// Never throws: when the call fails the app runs with safe defaults (all optional features off).
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export type PlatformFeatures = {
  googleAuth: boolean
  giphy: boolean
  livekit: boolean
  recording: boolean
  transcribe: boolean
  turnstileSiteKey: string | null
  vapidPublicKey: string | null
  stripe: boolean
  /** G-25: the minimum age in Terms section 3 (backend app/legal.py MINIMUM_AGE). No checkbox asks for it since 2026-10-06. */
  minimumAge: number
}

export type PlatformStatus = {
  maintenance: { message: string; startsAt: string | null } | null
  minWebBuild: number
  features: PlatformFeatures
}

export const DEFAULT_PLATFORM_STATUS: PlatformStatus = {
  maintenance: null,
  minWebBuild: 0,
  features: {
    googleAuth: false,
    giphy: false,
    livekit: false,
    recording: false,
    transcribe: false,
    turnstileSiteKey: null,
    vapidPublicKey: null,
    stripe: false,
    minimumAge: 16,
  },
}

export const PLATFORM_STATUS_KEY = ['platform', 'status'] as const

/** Merges a partial or odd payload over the defaults. */
export function normalizePlatformStatus(raw: unknown): PlatformStatus {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<PlatformStatus>
  const m = r.maintenance
  return {
    maintenance:
      m && typeof m === 'object' && typeof m.message === 'string'
        ? { message: m.message, startsAt: typeof m.startsAt === 'string' ? m.startsAt : null }
        : null,
    minWebBuild: Number.isFinite(Number(r.minWebBuild)) ? Number(r.minWebBuild) : 0,
    features: { ...DEFAULT_PLATFORM_STATUS.features, ...(r.features && typeof r.features === 'object' ? r.features : {}) },
  }
}

export async function fetchPlatformStatus(): Promise<PlatformStatus> {
  try {
    return normalizePlatformStatus(await api.get<unknown>('/api/platform/status'))
  } catch {
    return DEFAULT_PLATFORM_STATUS
  }
}

export function usePlatformStatus(): PlatformStatus {
  const { data } = useQuery({
    queryKey: PLATFORM_STATUS_KEY,
    queryFn: fetchPlatformStatus,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  })
  return data ?? DEFAULT_PLATFORM_STATUS
}

/** "scheduled" before startsAt, "active" once it has started (or when no start time is given). */
export function maintenancePhase(status: PlatformStatus, now = Date.now()): 'none' | 'scheduled' | 'active' {
  const m = status.maintenance
  if (!m) return 'none'
  if (m.startsAt && new Date(m.startsAt).getTime() > now) return 'scheduled'
  return 'active'
}

export function useFeature<K extends keyof PlatformFeatures>(key: K): PlatformFeatures[K] {
  return usePlatformStatus().features[key]
}
